import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { User } from '@sentry/angular';
import { DocusignService } from '@lmc-app/core/services/docusign.service';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig} from '@ngx-formly/core';
import { callLoader, createDeliveryChannel, flagMissingControls, getFormlyChangedValues, getMissingFields, hasClearedRequiredFields, isValidData, localizeDateFields, mapRecipientForEnvelopeUpdate, MissingField, revealMissingFields } from '../helper-functions/docusign-utils';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AgreementsValidationComponent } from './agreements-validation/agreements-validation.component';
import { ConfirmDocusignDialogComponent } from '../confirm-docusign-dialog/confirm-docusign-dialog.component';
import { BehaviorSubject, delay, distinctUntilChanged, finalize, firstValueFrom, forkJoin, fromEvent, map, of, Subject, Subscription, switchMap, take, takeUntil, timer } from 'rxjs';
import { ModuleFieldPicklist } from '@lmc-app/shared/interface/module-field-picklist';
import { Agency } from '@lmc-app/shared/interface/agency.interface';
import { ZohoService } from '@lmc-app/core/services/zoho.service';
import { AgreementsFormOptions } from './form-interfaces/agreements-form-options.interface';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { DocusignEnvelopeRecipients } from '../interface/docusign-envelope-recipients-interface';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { highlightPDFText } from '../helper-functions/pdf-text-highlight';
import { getPrimaryAgreementFields } from './form-interfaces/agreements-primary-interface';
import { getCoAppAgreementFields } from './form-interfaces/agreements-coapp-interface';
import { getFundedDetailsAgreementFields } from './form-interfaces/agreements-funded-details-interface';
import { SideNavigationService } from '../../../core/services/side-navigation.service';
import { AgreementLoanCalculatorService } from './services/agreements-calculator.service';
import { hasRequiredContactChanges } from '../helper-functions/docusign-utils';
import dayjs from 'dayjs';
import { RecipientOrderConfigService } from './services/recipient-order-config.service';
import { sortTargetSequence } from '../helper-functions/docusign-utils';
import { ActivatedRoute } from '@angular/router';
import { getRecipientList } from './form-interfaces/agreements-recipient-interface';
import { AGREEMENT_TEMPLATE_ROW_ID, PROVINCE_NAME_TO_CODE } from '../constants/docusign-constants';
import { ListenerService } from '@lmc-app/core/services/listener.service';
import { DocusignGuideComponent } from '../docusign-guide/docusign-guide.component';

@Component({
  selector: 'app-agreements',
  templateUrl: './agreements.component.html',
  styleUrls: ['./agreements.component.scss']
})
export class AgreementsComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input() selectedTab: string | null = null;
  @Input() application: any;
  @Output() applicationChange = new EventEmitter();
  @Input() currentUser: User;
  @Output() currentUserChange = new EventEmitter();
  @Output() requestedSideNavChange = new EventEmitter<string>();

  constructor(
    private _docusignService: DocusignService,  
		private _agreementValidationDialog: MatDialog,    
		private _recipientConfig: RecipientOrderConfigService,  
    private readonly zohoService: ZohoService, 
    private readonly apiService: LegacyApiService,
    private _sidenavService: SideNavigationService,  
    private route: ActivatedRoute,       
    private _listenerService: ListenerService,    
    private _docusignGuide: MatDialog,        
  ) { }

  formlyForm = new FormGroup({});   // Initialize the FormGroup correctly
  formlyFields: FormlyFieldConfig[] = [];   // separate variable for the field configuration array
  recipientForm = new FormGroup({});   // Recipients render in their own view, outside the accordion
  recipientFields: FormlyFieldConfig[] = [];
  showRecipients = false;
  dataModel = {};   // Holds form data
  missingFields: MissingField[] = [];   // Required fields with no value, blocks Send

  // Blocks Update All: saving would wipe a required value that was there before
  get hasClearedRequiredFields(): boolean {
    return hasClearedRequiredFields(this.missingFields, this.originalData);
  }
  showFiller = false;
  // options: FormlyFormOptions = {};

  private formOptions: Partial<AgreementsFormOptions>;
  private readonly _dialog = inject(MatDialog);
  private agencies$ = new BehaviorSubject<Agency[]>([]);
	private creditors$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
  private salesAgents$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
	private provinces$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);
  private readonly _bottomSheet = inject(MatBottomSheet);
  private readonly _snackBar = inject(MatSnackBar);
  private maxCalendarDate = new Date(new Date().getFullYear() - 1, 11, 31);
  private _agreementCalculatorService = inject(AgreementLoanCalculatorService);
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroy$ = new Subject<void>();
  
  missingData: any;
  _selectedTab:string;
  pdfSource = null;  
  loaderText = 'Loading agreement...';   // PDF pane loader message: (Deleting ->) Creating -> Updating -> Loading
  // ms = how long each message stays before the next one; the last one stays until the PDF arrives
  private readonly envelopeCreateStages = [
    { text: 'Creating agreement...', ms: 2000 },
    { text: 'Updating agreement...', ms: 5000 },
    { text: 'Loading agreement...', ms: 0 },
  ];
  private readonly envelopeUpdateStages = [
    { text: 'Deleting agreement...', ms: 2000 },
    ...this.envelopeCreateStages,
  ];
  private loaderStageTimer?: Subscription;
  isPdfLoaded = false;   // Set by the viewer's pdfLoaded event once the PDF has fully rendered

  get isDraftReady(): boolean {
    return !!this.createdEnvelopeId && !!this.pdfSource && this.isPdfLoaded;
  }
  originalData:any;
  isSahaApp: boolean;
  isValidDataPanes: boolean;
  missingInfo:any;
  confirmRecipients = true;   // Step 1 is unused; recipients (step 2) show immediately
  createdEnvelopeId: string;
  createdResponse: any;
  currentStep:number = 2;   // Create steps; step 1 (missing-info panel) is kept but unused incase need it back, flow starts at step 2
  // isSkipped: boolean = false;
  updateAvailable: boolean = false;
  isSavingChanges: boolean = false;
  private recipientsReordered = false;
  readonly templateChangeData = ['fullprovince'];
  ref: any;

  private subSideNav!: Subscription;    
  private saveTrigger$ = new Subject<string>(); 
  isSideUpdate: boolean = false;   
  getSavedRecipientOrder:string[] = [];
  private sseSubscription!: Subscription;
  protected urlAppId: string = '';
  private subs = new Subscription();

  ngOnInit(): void {
    this.urlAppId = this.route.snapshot.paramMap.get('id') || '';    
    this.subs.add(
      this._listenerService.on<string>('agreementUpdateCalc').subscribe((calcModel) => {
        // Do changes only if its on create tab and has response from calculator
        if (calcModel && this.isCreateView) {
          this.pdfSource = null;
          this.startLoaderStages(this.envelopeUpdateStages);
          const recipients = calcModel?.['recipients'] ;
          const applicationId = calcModel?.['originalData']?.['id'];
          this._docusignService.getUpdatedZohoApplication(applicationId).subscribe(async res => {  
            if(res){
              this.application = res?.data?.[0] ;
              await this.normalizeAgreementData(this.application);
              if (recipients) {
                this.dataModel['recipients'] = recipients;
              }              
              this.recreateAgreementDocument(this.dataModel, this.currentUser);
            }      
          })
        }
      })
    );
		
    // On Agreements side nav click go back to the list
    this.subs.add(
      this._listenerService.on<boolean>('agreementsListTabSelected').subscribe(() => {
        this.showRecipients = false;
        this.isCreateView = false;
      })
    );

    this.initializeFormConfig();
    this.subSideNav = this._sidenavService.sidenav$.subscribe(value => {
      // console.log('Side-nav selected tab:', value);
      this.requestedSideNavChange.emit(value);
    });
  }

  // Update existing Envelope
  saveChanges(model: any, skipRecreate: boolean = false){
    if (this.hasClearedRequiredFields) return;   // Never save over a required value the user cleared
    const changes = this.getChangedFields(this.originalData, model);

    // Two combination if changes && updateAvailable has to be false
    if (Object.keys(changes).length === 0 && !this.updateAvailable) {
      return;
    }
    this.isSavingChanges = true;
    // this.ref = callLoader(this._bottomSheet, 'Saving changes...');
    // Referral edit (skipRecreate) only saves to Zoho, so keep the current PDF on screen
    if (!skipRecreate) {
      this.pdfSource = null;
      this.startLoaderStages(this.envelopeUpdateStages);
    }

    const isTemplateAffectingChange = this.hasTemplateAffectingChange(changes);
    this.handleFieldChange(changes, model, isTemplateAffectingChange, skipRecreate);
  }

  private hasTemplateAffectingChange(changes: Record<string, { from: any; to: any }>): boolean {
    return Object.keys(changes).some((key) => this.templateChangeData.includes(key.toLowerCase()));
  }

  private handleFieldChange(changes: Record<string, { from: any; to: any }>, model: any, isTemplateAffectingChange: boolean, skipRecreate: boolean = false) {
    // Get updated Deal if changes on Firstname/Middlename/Lastname
    const DealNameValue = (hasRequiredContactChanges(changes))
      ?  { [model['DealName'].zoho_field_name]: [model['FirstName'].value,model['MiddleName'].value,model['LastName'].value].filter(Boolean).join(' ') }
      : null;

    // Sync dataModel.DealName so the recreated document/envelope uses the new changes instead of the initial stale load
    if (DealNameValue) {
      model['DealName'] = { ...model['DealName'], value: DealNameValue[model['DealName'].zoho_field_name] };
    }
    const fullProvinceName = changes['FullProvince']?.to?.value;
    const ProvinceValue = fullProvinceName
      ? { Province: PROVINCE_NAME_TO_CODE[fullProvinceName], Province1: fullProvinceName }
      : null;
    const zohoUpdateData = Object.assign(
      {},
      ...Object.values(changes).map(({ to }: { to: any }) => ({ [to.zoho_field_name]: to.value })),
      (!!DealNameValue ? DealNameValue : {}),
      (!!ProvinceValue ? ProvinceValue : {}),
      { Override_Type: `${this.currentUser?.full_name} (${dayjs().format('YYYY-MM-DD HH:mm:ss')})`}
    )

    // Update Zoho Changes
    this.saveZohoChanges(this.application?.id, zohoUpdateData, model?.['ContactId']?.value, changes, isTemplateAffectingChange, skipRecreate);
  }

  private saveZohoChanges(applicationId: string, zohoUpdateData: any, contactId: string, changes: Record<string, { from: any; to: any }>, isTemplateAffectingChange: boolean, skipRecreate: boolean = false){
    this._docusignService.saveDocusignZohoChanges(applicationId, zohoUpdateData, contactId).pipe(
      finalize(() => {
        // this.ref.dismiss();
      })
    ).subscribe({
      next: (res) => {
        const isSuccess = res?.data?.[0]?.status === 'success';
        if(isSuccess){
          this.originalData = JSON.parse(JSON.stringify(this.dataModel));
          if (!skipRecreate) {
            this.recreateAgreementDocument(changes, this.currentUser);
          } else {
            this.isSavingChanges = false;
          }
          this.applicationChange.emit(this.application);
        } else {
          this.isSavingChanges = false;
          // this.ref?.dismiss();
          if (!skipRecreate) this.previewAgreement(this.createdEnvelopeId);   // Nothing was deleted yet, reload the current PDF
        }
      },
      error: (err) => {
        console.error(err);
        this.isSavingChanges = false;
        if (!skipRecreate) this.previewAgreement(this.createdEnvelopeId);
      }
    })
  }

  // Envelope create/update runs as one backend request, so step the loader text while it runs
  private startLoaderStages(stages: { text: string; ms: number }[]): void {
    this.loaderStageTimer?.unsubscribe();
    this.loaderStageTimer = new Subscription();
    let offset = 0;
    stages.forEach(({ text, ms }) => {
      this.loaderStageTimer.add(timer(offset).subscribe(() => this.loaderText = text));
      offset += ms;
    });
  }

  private recreateAgreementDocument(agreementData: Record<string, { from: any; to: any }>, userInfo: any) {
    const recipients = agreementData['recipients']?.to ?? this.dataModel['recipients'];
    const envelopeData = {
      ...this.dataModel,
      envelopeRecipients: this.getUpdatedRecipients(recipients),
      dbMainEnvelope: {
        application_id: this.application.id,
        applicant_type: 'PRIMARY',
        name: this.dataModel['DealName'].value,
        client_email: this.dataModel['Email'].value,
        via_email: true,
        via_sms: false,
        via_whatsapp: false,
      },
      templateData: { template_row_id: AGREEMENT_TEMPLATE_ROW_ID }
    };

    this.deleteEnvelope({ ...this.dataModel, envelope_id: this.createdEnvelopeId }, userInfo);
    this._docusignService.createCompositeEnvelope(envelopeData, userInfo).pipe(
      finalize(() => {
        // this.ref?.dismiss();
      })
    ).subscribe({
      next: (response) => {
        this.updateAvailable = false;
        const newEnvelopeId = response?.['jsonData']?.['docusign_res']?.['envelopeId'];
        this.createdEnvelopeId = newEnvelopeId;
        this.dataModel = { ...this.dataModel, envelope_id: newEnvelopeId };
        this.previewAgreement(this.createdEnvelopeId);
        // this._listenerService.emit('aggreementNewUpdate', newEnvelopeId);    // Trigger pdf preview reload
      },
      error: (err) => {
        console.error(err);
        this.isSavingChanges = false;
      }
    });
  }

  private getUpdatedRecipients(recipients: any[]): any[] {
    // Recipients from normalizeAgreementData (e.g. after the loan calculator) are still form fields ({ value, field }),
    // not DocuSign recipients, so build them the same way createCompositeEnvelope does
    if (recipients?.some((r) => r?.delivery_option)) {
      const savedOrder = this.getConfigForFile('recipient', ['manager', 'client', 'agent', 'co-applicant']);
      return sortTargetSequence(this.createEnvelopeRecipients(recipients), 'roleName', savedOrder);
    }
    const dealName = this.dataModel['DealName']?.value;
    return recipients.map((r) => mapRecipientForEnvelopeUpdate(r, dealName));
  }
  
  async ngOnChanges(changes: SimpleChanges) {
    if(changes.application?.currentValue && !this.isSideUpdate){
      const previousApplicationId = changes.application.previousValue?.id;
      const newApplicationId = changes.application.currentValue?.id;
      const isDifferentApplication = !!previousApplicationId && previousApplicationId !== newApplicationId;

      this.application = changes.application?.currentValue;
      if (this.application) {
        ['agreements','agreements-create','agreements-list']?.includes(this.selectedTab);
        if (isDifferentApplication) {
          this.resetAgreementViewState();
        }
        this.loadDropdownOptions();
        await this.normalizeAgreementData(this.application);
        this.initStep();
      }
    }

    if (changes.currentUser?.currentValue) {
      this.currentUser = changes.currentUser?.currentValue;
    }
  }

  // Clear out state tied to the previous application 
  private resetAgreementViewState(): void {
    this.currentStep = 2;       // For missing validation 1
    this.confirmRecipients = true;        // For missing validation false    
    this.createdEnvelopeId = null;
    this.createdResponse = null;
    this.pdfSource = null;
    this.isSent = false;
    this.showRecipients = false;
    this.isCreateView = false;
    this.updateAvailable = false;
    this.isSavingChanges = false;
    this.recipientsReordered = false;
  }

  onApplicationChanged(application:any) {
    this.applicationChange.emit(application);
  }

  async normalizeAgreementData(application:any){
    return new Promise<void>((resolve, reject) => {
       this._docusignService.normalizeAgreementData(application, this.currentUser).subscribe({
        next: (res) => {
          localizeDateFields(res?.[0]);
          this.setNormalizedAgreementData(res?.[0]);
          resolve();
        },
        error: (err) => reject(err)
      });
    });
  }

  async updateNormalizeAgreementData(application:any): Promise<any>{
    return new Promise((resolve, reject) => {
      const sub = this._docusignService.normalizeAgreementData(application, this.currentUser).subscribe({
        next: (data) => {
          sub.unsubscribe(); // Clean up memory
          resolve(data?.[0]);     // Send data out of the promise
        },
        error: (err) => reject(err)
      });
    });
  }

  previewAgreement(envelopeId:string){
    this.pdfSource = null;
    this.isPdfLoaded = false;
    this.loaderStageTimer?.unsubscribe();   // Stop any pending stage so it can't overwrite "Loading"
    this.loaderText = 'Loading agreement...';
    // Keep "Loading agreement..." visible at least 2s so it doesn't just flash
    forkJoin([this._docusignService.streamEnvelope(envelopeId), timer(2000)]).subscribe({
      next: ([blob]: [Blob, number]) => {
        this.pdfSource = blob;
        this.isSavingChanges = false;
      },
      error: (err) => console.error("PDF Load Error:", err)
    });
  }

  sendAgreement(envelopeId:string){
    const ref = callLoader(this._bottomSheet, 'Sending envelope');
    this._docusignService.sendEnvelope(
      {
        envelope_id: envelopeId, 
        envelopeStatus: "Created", 
        envelopeData: [], 
        envelopeIsEdited: false, 
        userInfo: this.currentUser
      }
    ).pipe(
      finalize(() => {
        ref.dismiss(); 
      })
    ).subscribe({
      next: (response)=>{
        this.isSent = true;
        this.showRecipients = false;
      }
    })
  }
  
  private async initializeFormConfig() {
    this.formOptions = {
      agencies$: this.agencies$,
      creditors$: this.creditors$,
      salesAgents$: this.salesAgents$,
      provinces$: this.provinces$,
      getAgencyName: (agencyId: string) => this.getAgencyNameById(agencyId),
      onUpdate: (model: any, skipRecreate?: boolean) => {

        // this.createCompositeEnvelope()
        this.saveChanges(model, skipRecreate)
      },
      hasChanges: (model: any) => Object.keys(this.getChangedFields(this.originalData, model)).length > 0,
      onRecipientsReorder: () => {
        this.recipientsReordered = true;
        this.updateAvailable = true;
      },
      onRecipientsLoad: (recipients: any[]) => {
        this.originalData.recipients = JSON.parse(JSON.stringify(recipients));
      },
    }
    await this.mapFormWithCard()
    this.formlyForm.valueChanges.subscribe(() => {
      this.syncProvinceCode();
      // Recompute so filled fields drop off and cleared required fields block Send again
      this.missingFields = getMissingFields(this.dataModel);
      flagMissingControls(this.formlyFields, this.missingFields);
      this.updateAvailable = this.recipientsReordered
        || Object.keys(this.getChangedFields(this.originalData, this.dataModel)).length > 0;
    });
  }

  private syncProvinceCode(): void {
    const fullProvinceName = this.dataModel?.['FullProvince']?.value;
    const code = PROVINCE_NAME_TO_CODE[fullProvinceName];
    if (!code) return;

    ['AddressProvince', 'Province'].forEach((key) => {
      if (this.dataModel?.[key] && this.dataModel[key].value !== code) {
        this.dataModel[key].value = code;
      }
    });

    if (this.dataModel?.['originalData']) {
      this.dataModel['originalData'].Province = code;
      this.dataModel['originalData'].Province1 = fullProvinceName;
    }
  }

  private static readonly META_MODEL_KEYS = ['originalData', 'userInfo', 'envelope_id', 'recipients'];  // Ignore the changes for this keys

  private getChangedFields(original: any, updated: any): Record<string, { from: any; to: any }> {
    const changes: Record<string, { from: any; to: any }> = {};
    // Added this.confirmRecipients
    if (!original || !updated) return changes;
    
    Object.keys(updated)
      .filter((key) => !AgreementsComponent.META_MODEL_KEYS.includes(key))
      .forEach((key) => {
        const originalValue = original[key];
        const updatedValue = updated[key];
        if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
          changes[key] = { from: originalValue, to: updatedValue };
        }
      });
    return changes;
  }

  private loadDropdownOptions() {
		this.getAgencies();
		this.getSalesAgents();
		this.getCreditors();
		this.getProvinces();
	}

  private getProvinces() {
		this.apiService.getModuleField('Deals', 'Province1').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.provinces$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getAgencies() {
		this.zohoService.getAgencies().subscribe({
			next: (res) => {
				this.agencies$.next(res);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getAgencyNameById(agencyId: string): string {
		if (!agencyId) return 'No Agency Selected';
		const agencies = this.agencies$.getValue();
		const agency = agencies.find((a) => a.id === agencyId);
		return agency?.Account_Name || 'Selected Agency';
	}

  private getCreditors() {
		this.apiService.getModuleField('Deals', 'Creditt').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.creditors$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  private getSalesAgents() {
		this.apiService.getModuleField('Deals', 'Sales_Agent').subscribe({
			next: (res) => {
				const filtered = res.pick_list_values.filter(
					(item: { display_value: string }) => item.display_value !== '-None-'
				);
				this.salesAgents$.next(filtered);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

  openFormEdit(formName:string){
    if(formName === "funded details"){
      // To be refactored later to make it emit only one, when saha with and without is merged into one calculator
      if(this.isSahaApp){
        this.requestedSideNavChange.emit('calculator');   // Calculator with SAHA fee
      } else{
        this.requestedSideNavChange.emit('oldCalculator');  // Calculator with no SAHA fee. 
      }
    } else{
      const dialogRef = this._agreementValidationDialog.open(AgreementsValidationComponent, {
        minWidth: '800px',
        autoFocus: true,
        disableClose: true,
        panelClass: 'hide-dialog-scrollbar',
        data: { 
          applicationId: this.application?.id, 
          formName: formName,  
          model: { ...this.dataModel }, 
          userInfo: this.currentUser, 
          formOptions: this.formOptions}
      });
      dialogRef.afterClosed().subscribe(result => {
        this.applicationChange.emit(this.application);
      });
    }
  }    
  
  onConfirmRecipient(){
    this.confirmRecipients = true;    
  }

  createCompositeEnvelope(){
    const getSavedRecipientOrder = this.getConfigForFile('recipient',['manager','client','agent','co-applicant']);
    // const ref = callLoader(this._bottomSheet, 'Creating agreement'); 
    this.pdfSource = null;
    // Envelope create also updates it on the backend, so move the loader to Updating while the request is still running
    this.startLoaderStages(this.envelopeCreateStages);
    let envelopeRecipients = this.createEnvelopeRecipients(this.dataModel['recipients']);

    envelopeRecipients = sortTargetSequence(envelopeRecipients,'roleName',getSavedRecipientOrder);
    
    this.saveIsSahaFile('isSahaFile',this.urlAppId, this.dataModel?.['HasSaha']);

    const envelopeData = {
      ...this.dataModel, 
      ...{envelopeRecipients: envelopeRecipients},
      ...{ dbMainEnvelope: 
        {
          application_id: this.application.id,        
          applicant_type: 'PRIMARY',
          name: this.dataModel['DealName'].value,
          client_email: this.dataModel['Email'].value,
          via_email: true,
          via_sms: false,
          via_whatsapp: false,
        },
        ...{templateData: { template_row_id: AGREEMENT_TEMPLATE_ROW_ID }}
      }
    };
    this._docusignService.createCompositeEnvelope(envelopeData, this.currentUser).pipe(
        finalize(() => {
          // ref.dismiss(); 
          this.loaderStageTimer?.unsubscribe();
        })
      ).subscribe({
      next: (response)=>{
        this.updateAvailable = false;
        this.createdResponse = response['jsonData'];
        this.createdResponse.envelope_id = response['jsonData']['docusign_res']['envelopeId'];
        this.createdEnvelopeId = response['jsonData']['docusign_res']['envelopeId'];
        this.dataModel = { ...this.dataModel, envelope_id: this.createdEnvelopeId };
        this.mapFormWithCard();
        setTimeout(() => revealMissingFields(this.formlyFields, this.missingFields));   // Wait for formly to build the controls
        this.previewAgreement(this.createdEnvelopeId)
      },
      error: (err) => {
        this.openSnackBar('error',`Create Envelope failed:  ${err?.message || err}`);
      }
    })
  }

  // Later use when required
  // // async updateCompositeEnvelope(envelopeData:any, userInfo:any){
  // //   this._docusignService.updateCompositeEnvelope(envelopeData, userInfo).pipe(
  // //       finalize(() => {
  // //         this._bottomSheet.dismiss(); 
  // //       })
  // //     ).subscribe({
  // //     next: (response)=>{
  // //       // // this.createdResponse = response['jsonData'];
  // //       // // this.createdResponse.envelope_id = response['jsonData']['docusign_res']['envelopeId'];
  // //       // // this.createdEnvelopeId = response['jsonData']['docusign_res']['envelopeId'];
  // //       // // this.previewAgreement(this.createdEnvelopeId);
  // //       this.previewAgreement('3b7d25aa-167c-8ac8-81d7-9bd63e6d1940')
  // //     },
  // //     error: (err) => {
  // //       this.openSnackBar('error',`Create Envelope failed:  ${err?.message || err}`);
  // //     }
  // //   })
  // // }
  
  createEnvelopeRecipients(recipients:any){
    let docusignEnvelopeRecipients: Partial<DocusignEnvelopeRecipients> ={};
    const preparedRecipients = [];
    for(let i=0;i<recipients.length;i++){
      const selectedDelivery = Object.keys(recipients[i].delivery_option).filter(key => recipients[i].delivery_option[key] === true).join('+');
      const channelType = {deliveryType:'multi',deliveryOptions: selectedDelivery};
      const currentRecipient = recipients[i];
      docusignEnvelopeRecipients = {
        name: currentRecipient?.name.value?.toUpperCase(),
        ...(['default','multi']?.includes(channelType?.deliveryType?.toLowerCase()))
        ? {
          email: currentRecipient?.email?.value,
          emailNotification: {
            emailSubject: `Loan Agreement - ${this.dataModel['DealName'].value}`,
            emailBody: `Hi ${currentRecipient?.name.value?.toUpperCase()},<br><br>Please review and sign, loan agreement of ${this.dataModel['DealName'].value} with LM Credit Inc.<br><br>Thank you`,
            supportedLanguage: "en"
          }
        }
        : {},
        roleName: currentRecipient?.role,
        recipientId: (i+1).toString(), 
        routingOrder: (i+1).toString(), 
        ...(channelType?.deliveryType?.toLowerCase() === 'multi')
        ? createDeliveryChannel(currentRecipient?.mobile.value,channelType)
        : {}
      };
      preparedRecipients.push(docusignEnvelopeRecipients)
    }      
    return preparedRecipients;
  }
  
  handleNext(): void {
    // Moved to Send button
    // // if (this.currentStep === 3) {
    // //   // Last step: button reads "Send" — do the actual send instead of advancing
    // //   this.sendAgreement(this.createdEnvelopeId);
    // //   return;
    // // }

    if (this.currentStep === 2) {
      // Moving from step 2 -> 3 happens only once the on-screen data is confirmed to match latest Zoho
      this.createWithLatestData();
      return;
    }

    this.currentStep++;
  }

  get isSendDisabled(): boolean {
    return !this.dataModel || !this.dataModel?.['hasLoanCalculatorInfo'];
  }

  get isNextButtonDisabled(): boolean {
    return this.currentStep === 3 ? this.isSendDisabled : (this.isNextDisabled || this.isRefreshingData);
  }

  get isNextDisabled(): boolean {
    return this.currentStep === 1 && !this.isValidDataPanes;
  }

  handleSkip(): void {
    // Skip step 1 entirely, go straight to recipients
    this.confirmRecipients = true;
    this.currentStep = 2;
  }

  isStep1Complete(): boolean {
    return this.isValidDataPanes; 
  }

  private initStep(): void {
    if (this.currentStep !== 1) {
      return;
    }

    if (this.isValidDataPanes === true) {
      this.confirmRecipients = true;
      this.currentStep = 2;
    }
  }

  resetNext() {
    this.currentStep = 2;       // Missing validation 1
    this.confirmRecipients = true;      // Missing validation false
    this.isCreateView = false;
  }

  openSnackBar(messageType: string, message: string){
    if(messageType === 'error'){
      return this._snackBar.open(message, SnackbarConstants.ACTION_DISMISS,SnackbarConstants.DURATION_DISMISS_ALIVE,);
    } else{
      return this._snackBar.open(message, SnackbarConstants.ACTION_DISMISS,SnackbarConstants.DURATION_DISMISS_SHORT,);
    }
  }

  onTextLayerRendered(event: any): void {
    const containerDiv = event.source?.div || event.element;
    if (!containerDiv || !this.dataModel) return;

    // Map your dictionary's data values into a simple array of strings
    const keywordsToMatch = Object.values(this.dataModel)
      .map(item => String(item['value']));

    // Execute the reusable utility
    highlightPDFText({
      container: containerDiv,
      keywords: keywordsToMatch,
      className: 'custom-bold-highlight'
    });
  }

  discardAgreement(){
    this._dialog.open(ConfirmDocusignDialogComponent, {
      data: {
        title:`Do you want to delete agreement?`,
        type: "",
        fileName: "",
      },
    })
    .afterClosed()
    .subscribe((result) => {
      if(result){
      const ref = callLoader(this._bottomSheet, 'Deleting agreement');
      this._docusignService.deleteEnvelope({envelopeData: this.createdResponse, userInfo: this.currentUser, docusignType: 'AGREEMENT'}).pipe(
          finalize(() => {
            ref.dismiss(); 
          })
        ).subscribe({
          next: (response)=>{
            if(response?.status === 201 || response?.status === 200){    
              this.currentStep = 0;
              this.confirmRecipients = false;
              this.openSnackBar('success',response?.message);   
            }else{
              this.openSnackBar('error',response)
            } 
          }
        })
      }
    })  
  }

  deleteEnvelope(envelopeData:any, userInfo:any){
    this._docusignService.deleteEnvelope({envelopeData: envelopeData, userInfo: userInfo, docusignType: 'AGREEMENT'}).subscribe({
      next: (response)=>{
        
      }
    })
  }

  async mapFormWithCard(){
    this.recipientFields = getRecipientList(this.formOptions, this.createdEnvelopeId, 'col-12 field-sm');
    this.formlyFields = [
      ...getPrimaryAgreementFields(this.formOptions,this.maxCalendarDate, true, 'col-12 field-sm'),
      ...getCoAppAgreementFields(this.formOptions,this.maxCalendarDate, true, 'col-12 field-sm'),
      ...getFundedDetailsAgreementFields(this.formOptions,true, 'col-sm-12 col-lg-6')      
    ]
  }

  // Call this whenever the file is (re)added/created, or when the config changes
  saveConfigForFile(fileKey: string, config: string[]) {
    this._recipientConfig.setConfig(fileKey, config);
  }

  // Call this when loading/recreating the file to check for a previous config
  getConfigForFile(fileKey: string, defaultConfig: string[] = []): string[] {
    return this._recipientConfig.getConfig(fileKey) ?? defaultConfig;
  }

  saveIsSahaFile(indicator:any, fileKey: string, value: boolean){
    this._agreementCalculatorService.setConfig(indicator, fileKey, value);
  }

  isScrolled = false;
  // Summary content (.scrollable-content) scrolled: shows the section header bar shadow
  isPageScrolled = false;

  onScroll(el: HTMLElement) {
    this.isScrolled = el.scrollTop > 0;
  }

  isSent:boolean = false;

  get statusTone(): 'draft' | 'sent' {
    return this.isSent ? 'sent' : 'draft';
  }

  onSend(){
    this.sendAgreement(this.createdEnvelopeId);
  }

 onRefresh(){
    this.previewAgreement(this.createdEnvelopeId)
    this._listenerService.emit('recipientStatus', true);
  }

  isGuideOpen: boolean = false;
  openDocusignGuide(){
    this.isGuideOpen = true;
    const dialogRef = this._docusignGuide.open(DocusignGuideComponent, {
      width: 'calc((85vh - 48px) * 0.85)',
      height: '85vh',
      minWidth: '800px',
      minHeight: '400px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: true,
      panelClass: ['hide-dialog-scrollbar', 'custom-dynamic-modal'],
    });
    dialogRef.afterClosed().subscribe(() => this.isGuideOpen = false);
  }

  onBackToList(){
    this.showRecipients = false;
    this.resetNext();
  }
  
  ngAfterViewInit(): void {
    // Show the header bar shadow only once the summary content has scrolled
    const scrollContainer = this._elementRef.nativeElement.closest('.scrollable-content');
    if (!scrollContainer) {
      return;
    }
    this.subs.add(
      fromEvent(scrollContainer, 'scroll', { passive: true })
        .pipe(
          map(() => scrollContainer.scrollTop > 0),
          distinctUntilChanged()
        )
        .subscribe((isScrolled) => (this.isPageScrolled = isScrolled))
    );
  }

  ngOnDestroy() {
    this.saveTrigger$.complete();
    if (this.sseSubscription) {
			this.sseSubscription.unsubscribe();
		}
    this.subs.unsubscribe();
    this.loaderStageTimer?.unsubscribe();
  }

  isRefreshingData = false;   // Fetching latest Zoho data, blocks Next so nothing is created from data still loading
  private static readonly TIME_STAMPED_KEYS = ['CreatedDate', 'CreatedTime'];   // Regenerated on every normalize, not application data

  // Fill the screen state from a normalized agreement record
  private setNormalizedAgreementData(data: any): void {
    this.dataModel = {};
    this.originalData = {};
    this.missingData = data?.missingFields ;
    this.missingInfo = data?.missingFields.filter(item=>item.title.toLowerCase() !== 'optional').map(item => item.title);
    this.isSahaApp = data?.HasSaha ;
    this.isValidDataPanes = data?.isValidPane;
    this.originalData = JSON.parse(JSON.stringify(data ?? {})); // deep clone so nested field objects aren't shared with dataModel
    this.dataModel = {...data, ...{originalData: this.application}, ...{userInfo: this.currentUser}} ;
    this.missingFields = getMissingFields(this.dataModel);
    this.recipientsReordered = false;
    this.updateAvailable = false;
  }

  // Get the application straight from Zoho and normalize it, the @Input can still hold pre-save data
  private async fetchLatestAgreementData(): Promise<{ application: any; data: any } | null> {
    const res = await firstValueFrom(this._docusignService.getUpdatedZohoApplication(this.application?.id));
    const application = res?.data?.[0];
    if (!application) return null;
    const data = await this.updateNormalizeAgreementData(application);
    localizeDateFields(data);
    return { application, data };
  }

  // Rebuild the screen from latest Zoho data, falls back to the @Input data if the fetch fails
  private async loadLatestAgreementData(): Promise<void> {
    this.isRefreshingData = true;
    try {
      const latest = await this.fetchLatestAgreementData();
      if (latest) {
        this.application = latest.application;
        this.setNormalizedAgreementData(latest.data);
      } else {
        await this.normalizeAgreementData(this.application);
      }
    } catch (err) {
      console.error(err);
      await this.normalizeAgreementData(this.application);
    } finally {
      this.isRefreshingData = false;
    }
  }

  // Create only when what's on screen matches latest Zoho, otherwise show the latest data and let the user review it first
  private async createWithLatestData(): Promise<void> {
    if (this.isRefreshingData) return;
    this.isRefreshingData = true;
    try {
      const latest = await this.fetchLatestAgreementData();
      if (!latest) {
        this.openSnackBar('error', 'Could not load the latest application data. Please try again.');
        return;
      }
      if (this.hasAgreementDataChanged(this.dataModel, latest.data)) {
        this.showLatestAgreementData(latest.application, latest.data);
        this.openSnackBar('error', 'Application data was updated. Please review and click Next again.');
        return;
      }
      this.application = latest.application;
      // Always save the current on-screen order so a new Create doesn't reuse the order from a previous one
      this.saveConfigForFile('recipient',this.dataModel?.['recipients'].map(recipient=>recipient?.role?.toLowerCase()));
      this.createCompositeEnvelope();
      this.currentStep = 3;
    } catch (err) {
      console.error(err);
      this.openSnackBar('error', `Could not load the latest application data: ${err?.message || err}`);
    } finally {
      this.isRefreshingData = false;
    }
  }

  // Swap in latest data but keep the recipient choices made on this screen (order is kept by the recipients component)
  private showLatestAgreementData(application: any, data: any): void {
    const recipientKey = (r: any) => `${r?.role}|${r?.email?.value}`;
    const deliveryOptions = new Map<string, any>((this.dataModel?.['recipients'] ?? []).map(r => [recipientKey(r), r?.delivery_option]));
    (data?.recipients ?? []).forEach(r => {
      const previous = deliveryOptions.get(recipientKey(r));
      if (previous) r.delivery_option = { ...previous };
    });
    this.application = application;
    this.setNormalizedAgreementData(data);
  }

  // Compares field values and recipient details, ignoring recipient order/delivery options (local to this screen) and timestamps
  private hasAgreementDataChanged(current: any, latest: any): boolean {
    const fieldChanges = Object.keys(this.getChangedFields(current, latest))
      .filter(key => !AgreementsComponent.TIME_STAMPED_KEYS.includes(key));
    if (fieldChanges.length) return true;

    const recipientDetails = (recipients: any[]) => (recipients ?? [])
      .map(r => JSON.stringify(['role', 'firstName', 'middleName', 'lastName', 'name', 'email', 'mobile'].map(f => r?.[f]?.value ?? r?.[f] ?? null)))
      .sort();
    return JSON.stringify(recipientDetails(current?.recipients)) !== JSON.stringify(recipientDetails(latest?.recipients));
  }

  @ViewChild('createRecipientsDialog') createRecipientsDialog: TemplateRef<unknown>;
  private createRecipientsDialogRef: MatDialogRef<unknown>;

  // Create from the list: same fresh start as the side nav Create, but recipients are reviewed in a modal
  async openCreateRecipientsModal(): Promise<void> {
    if (!this.application) return;
    this.resetAgreementViewState();
    this.createRecipientsDialogRef = this._dialog.open(this.createRecipientsDialog, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'create-agreement-modal',   // resizable (see styles.scss)
      autoFocus: false,
    });
    await this.loadLatestAgreementData();
  }

  isCreateView = false;   // Agreements is one side nav item: list by default, create/preview after Submit in the modal

  // Submit in the modal: create the envelope, then swap the list for the preview
  async onCreateFromRecipientsModal(): Promise<void> {
    await this.createWithLatestData();
    if (this.currentStep !== 3) return;   // Data changed or fetch failed, keep the modal open with the latest data
    this.createRecipientsDialogRef?.close();
    this.isCreateView = true;
  }

}
